import * as React from 'react';
import classnames from 'classnames';
import * as toastr from 'toastr';


import DropZone, { DropzoneRef } from 'react-dropzone-esm';

import { Binding, Result } from 'core/infrastructure';
import { Convert } from 'core/utils';
import MimeTypes from '../MimeTypes';

import ErrorBlock from './ErrorBlock';
import { FormComponent } from './Form';
import { formatAsElementId } from './Utils';
import { Res } from 'core/api';

interface FileInfo {
	Data: string;
	FileName: string;
}

interface Props<T> extends React.HTMLProps<FileSelector<T>> {
	linkTo: Binding.PropertyPath<T>;
	extensions?: string[];
	label?: string;
	onValueChanged?: (fileName: string, fileContent: string) => void;
	boundValue?: FileInfo;
	formId?: string;
	validator?: (file: File) => string;
}

interface State {
	name: string;
	base64Content: string;
	errorMsg: string;
	isLoadingFile: boolean;
}

export class FileSelector<T> extends React.Component<Props<T>, State> implements FormComponent {

	private dropZone = React.createRef<DropzoneRef>();
	private ref = React.createRef<HTMLDivElement>();

	constructor(props: Props<T>) {

		super(props);

		if (props.boundValue && (typeof props.boundValue.FileName !== 'string' || typeof props.boundValue.Data !== 'string'))
			throw new Error('File selector esperaba una boundValue de la forma  { FileName: string, Data: string }');

		this.state = this.createStateFromProps(props);

		this.onDropFile = this.onDropFile.bind(this);
		this.onOpenFile = this.onOpenFile.bind(this);
		this.onDropAccepted = this.onDropAccepted.bind(this);
	}

	createStateFromProps(props: Props<T>): State {
		return {
			base64Content: props.boundValue ? props.boundValue.Data : '',
			name: props.boundValue ? props.boundValue.FileName : '',
			errorMsg: '',
			isLoadingFile: false
		};
	}

	componentDidMount() {
		$('*', this.ref.current!).addClass('needsclick');
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	getValue(): Result<FileInfo> {

		this.setState({ errorMsg: '' });

		if (this.props.required && this.state.name.length === 0) {
			this.setState({ errorMsg: Res.Msg.MSG_FILE_MUST_BE_SELECTED() });
			return Result.error();
		}

		if (this.props.required && this.state.base64Content.length === 0) {
			this.setState({ errorMsg: Res.Msg.MSG_INVALID_FILE_SIZE() });
			return Result.error();
		}

		return Result.ok({ FileName: this.state.name, Data: this.state.base64Content });
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	private async onDropFile(files: File[]) {
		const file = files[0];
		if (!file)
			return;

		const content = await Convert.blobToBase64String(file) || '';
		this.setState({ name: file.name, base64Content: content, isLoadingFile: false }, () => this.notifyValueChanged(file.name, content));
	}

	private notifyValueChanged(fileName: string, fileContent: string): void {
		this.props.onValueChanged?.(fileName, fileContent);
	}

	public onOpenFile() {
		this.dropZone.current!.open();
	}

	private onDropAccepted() {
		this.setState({ isLoadingFile: true });
	}

	render() {

		const label = this.props.label ? <label className='col-sm-4 control-label'>{this.props.label}</label> : null;

		const extensions = this.props.extensions ? this.props.extensions.map(x => '.' + x) : [];
		const acceptExtensions = extensions.map(x => '.' + x).join(',');
		const acceptMimeTypes = this.props.extensions ? this.props.extensions.map(x => MimeTypes[x]).join(',') : '';
		const accept = this.props.extensions ? acceptExtensions + ', ' + acceptMimeTypes : '*.*';

		const dropZoneClasses = classnames({ 'needsclick': true });
		return <div ref={this.ref} className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			{label}
			<div className={this.props.label ? 'col-sm-8' : 'col-sm-12'}>
				<DropZone
					disabled={this.state.isLoadingFile || this.props.disabled}
					onDropAccepted={this.onDropAccepted}
					onDrop={this.onDropFile}
					ref={this.dropZone}
					accept={{ [`${accept}`]: extensions }}
					multiple={false}
					noClick={false}
					noKeyboard={true}
					useFsAccessApi={false}
					validator={(file: File) => {
						const error = { message: this.props.validator ? this.props.validator(file) : '', code: '' };
						if (error.message)
							toastr.warning(error.message);

						return error.message ? error : null;
					}}
				>
					{({ getRootProps, getInputProps }) => (
						<div id={formatAsElementId(this.getPath(), this.props.formId || '')}
							className={dropZoneClasses}
							{...getRootProps()}>
							<div className='input-group'>
								<input type='text' value={this.state.name} className='form-control' disabled />
								<span className='input-group-btn'>
									<button type='button' className='btn btn-default' disabled={this.state.isLoadingFile || this.props.disabled}><i className='text-info fa fa-folder-open'></i></button>
								</span>
							</div>
							{this.state.isLoadingFile ? <small><i>{Res.Msg.MSG_LOADING_FILE()}</i></small> : null}
							<input {...getInputProps({})} style={{ display: 'none' }} />
						</div>
					)}
				</DropZone>
				<ErrorBlock msg={this.state.errorMsg} />
			</div>

		</div>;
	}
}
export type FileSelectorCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
